import {
  RestaurantConfig,
  PaymentMethod,
  OrderStatus,
  SystemConfig,
} from "../types/config";

import apiClient from "./api";

interface WeekSchedule {
  id: number;
  type: "delivery" | "pickup";
  day_of_week: number;
  open_hour: string;
  close_hour: string;
}

class ConfigService {
  /**
   * Get restaurant configuration
   */
  async getRestaurantConfig(): Promise<RestaurantConfig> {
    return await apiClient.get<RestaurantConfig>("/config/restaurant");
  }

  /**
   * Update restaurant configuration
   */
  async updateRestaurantConfig(configData: {
    name: string;
    address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    phone?: string;
    email?: string;
    website?: string;
    tax_rate: number;
    logo_url?: string;
    hours_of_operation?: string;
    delivery_radius?: number;
    minimum_delivery_amount?: number;
    delivery_fee?: number;
  }): Promise<RestaurantConfig> {
    return await apiClient.put<RestaurantConfig>(
      "/config/restaurant",
      configData,
    );
  }

  /**
   * Get week schedule
   */
  async getWeekSchedule(): Promise<WeekSchedule[]> {
    return await apiClient.get<WeekSchedule[]>("/config/week-schedule");
  }

  /**
   * Create week schedule
   */
  async createWeekSchedule(scheduleData: {
    type: "delivery" | "pickup";
    day_of_week: number;
    open_hour: string;
    close_hour: string;
  }): Promise<WeekSchedule> {
    return await apiClient.post<WeekSchedule>(
      "/config/week-schedule",
      scheduleData,
    );
  }

  /**
   * Update week schedule
   */
  async updateWeekSchedule(
    id: number,
    scheduleData: {
      type?: "delivery" | "pickup";
      day_of_week?: number;
      open_hour?: string;
      close_hour?: string;
    },
  ): Promise<WeekSchedule> {
    return await apiClient.put<WeekSchedule>(
      `/config/week-schedule/${id}`,
      scheduleData,
    );
  }

  /**
   * Delete week schedule
   */
  async deleteWeekSchedule(id: number): Promise<void> {
    return await apiClient.delete(`/config/week-schedule/${id}`);
  }

  /**
   * Get all payment methods
   */
  async getPaymentMethods(): Promise<PaymentMethod[]> {
    return await apiClient.get<PaymentMethod[]>("/config/payment-methods");
  }

  /**
   * Create a new payment method
   */
  async createPaymentMethod(methodData: {
    name: string;
    description?: string;
    requires_card_info?: boolean;
  }): Promise<PaymentMethod> {
    return await apiClient.post<PaymentMethod>(
      "/config/payment-methods",
      methodData,
    );
  }

  /**
   * Update an existing payment method
   */
  async updatePaymentMethod(
    id: number,
    methodData: {
      name?: string;
      description?: string;
      requires_card_info?: boolean;
    },
  ): Promise<PaymentMethod> {
    return await apiClient.put<PaymentMethod>(
      `/config/payment-methods/${id}`,
      methodData,
    );
  }

  /**
   * Delete a payment method
   */
  async deletePaymentMethod(id: number): Promise<void> {
    return await apiClient.delete<void>(`/config/payment-methods/${id}`);
  }

  /**
   * Get all order statuses
   */
  async getOrderStatuses(): Promise<OrderStatus[]> {
    return await apiClient.get<OrderStatus[]>("/config/order-statuses");
  }

  /**
   * Create a new order status
   */
  async createOrderStatus(statusData: {
    name: string;
    description?: string;
    color?: string;
  }): Promise<OrderStatus> {
    return await apiClient.post<OrderStatus>(
      "/config/order-statuses",
      statusData,
    );
  }

  /**
   * Update an existing order status
   */
  async updateOrderStatus(
    id: number,
    statusData: {
      name?: string;
      description?: string;
      color?: string;
    },
  ): Promise<OrderStatus> {
    return await apiClient.put<OrderStatus>(
      `/config/order-statuses/${id}`,
      statusData,
    );
  }

  /**
   * Delete an order status
   */
  async deleteOrderStatus(id: number): Promise<void> {
    return await apiClient.delete<void>(`/config/order-statuses/${id}`);
  }

  /**
   * Get complete system configuration
   */
  async getSystemConfig(): Promise<SystemConfig> {
    return await apiClient.get<SystemConfig>("/config/system");
  }

  /**
   * Update tax rate
   */
  async updateTaxRate(taxRate: number): Promise<void> {
    return await apiClient.put<void>(`/config/taxes?tax_rate=${taxRate}`);
  }
}

// Create singleton instance
export const configService = new ConfigService();
